import { Text } from '@magicschool/spellbook/components/Text';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as LabelPrimitive from 'radix-ui/label';

export type LabelProps = React.ComponentProps<typeof LabelPrimitive.Root> & VariantProps<typeof labelVariants>;

export const labelVariants = cva(
  [
    'flex',
    'items-center',
    'text-default-text-default',
    'select-none',
    'group-data-[disabled=true]:cursor-not-allowed',
    'group-data-[disabled=true]:text-disabled-foreground',
    'peer-disabled:cursor-not-allowed',
    'peer-disabled:text-disabled-foreground',
    'peer-required:after:content-["*"]',
    'peer-aria-[required=true]:after:content-["*"]',
    'peer-required:after:ml-1',
    'peer-aria-[required=true]:after:ml-1',
  ],
  {
    variants: {
      /** Shows the required asterisk when the label is not a sibling after its control, so `peer-required` cannot apply */
      required: {
        true: ['after:content-["*"]', 'after:ml-1'],
      },
    },
  },
);

/**
 * The standard label for form elements. **Always use this component instead of raw `<label>` or `<Text>` tags.**
 *
 * ### When to use
 * - Naming a form control that has no built-in label (`TextInput`, `Select`, `Textarea`), linked with `htmlFor`
 *
 * ### When NOT to use
 * - Headings or captions that are not tied to a form control: use `Text`
 * - Controls that render their own label from a `label` prop (`Checkbox`, `Switch`): do not add a second `Label`
 *
 * ### Why use Label (not raw `<label>`)
 * The Label component provides critical form integration that raw HTML labels lack:
 * - **Typography consistency**: Uses design system text variants (`label-1` / `body-2`)
 * - **Design tokens**: Colors, spacing, and typography follow the Spellbook design system
 *
 * ### Crucial for forms
 * Labels are essential for accessible forms:
 * - **Screen readers**: Announce the label when the input receives focus
 * - **Click target**: Clicking the label focuses the associated input
 * - **Required indicator**: Sighted users see `*` for required fields
 * - **State feedback**: Visual dimming indicates disabled fields
 *
 * ### Associating with form controls
 * Use `htmlFor` to associate the label with a form control:
 * ```tsx
 * <Label htmlFor="email">Email address</Label>
 * <TextInput id="email" type="email" required />
 * ```
 *
 * ### API
 * - `htmlFor`: ID of the associated form control (required for accessibility)
 * - Supports all native `<label>` attributes
 *
 * ### Peer/group state integration
 * Label automatically responds to input states via CSS selectors:
 * - `peer-required` / `peer-aria-[required=true]`: Shows asterisk
 * - `peer-disabled`: Dims text and changes cursor
 * - `group-data-[disabled=true]`: Responds to parent group's disabled state
 */
export const Label: React.FC<LabelProps> = ({ children, className, required, ...props }) => {
  return (
    <LabelPrimitive.Root data-spellbook-name="label" className={cn(labelVariants({ required }), className)} {...props} asChild>
      <Text variant="label-1" element="label">
        {children}
      </Text>
    </LabelPrimitive.Root>
  );
};
